import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../baseQuery';

/**
 * Operations module (/api/ops). One prefix for Master Admin and Zonal Manager —
 * the server scopes a manager to their schools, so unlike the admin slices this
 * one needs no role-derived base.
 */
export const opsApi = createApi({
	reducerPath: 'opsApi',
	baseQuery: axiosBaseQuery,
	tagTypes: [
		'OpsDashboard',
		'Handovers',
		'Handover',
		'Onboarding',
		'Readiness',
		'TrainerPool',
		'LabSetups',
		'LabSetup',
		'Inventory',
		'MaterialRequests',
		'Tickets',
		'Ticket',
		'MyTickets',
		'OwnLabSetup',
	],
	endpoints: (builder) => ({
		getOpsDashboard: builder.query<any, void>({
			query: () => ({ url: '/ops/dashboard' }),
			providesTags: ['OpsDashboard'],
		}),

		// ── Handover ──
		listHandovers: builder.query<any, { status?: string } | void>({
			query: (args) => ({
				url: '/ops/handovers',
				params: { status: (args as any)?.status || 'pending_review' },
			}),
			providesTags: ['Handovers'],
		}),
		getHandover: builder.query<any, string>({
			query: (id) => ({ url: `/ops/handovers/${id}` }),
			providesTags: ['Handover'],
		}),
		updateHandover: builder.mutation<any, { id: string; data: any }>({
			query: ({ id, data }) => ({
				url: `/ops/handovers/${id}`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: ['Handover', 'Handovers', 'Readiness', 'Inventory'],
		}),
		acceptHandover: builder.mutation<any, string>({
			query: (id) => ({ url: `/ops/handovers/${id}/accept`, method: 'POST' }),
			invalidatesTags: ['Handover', 'Handovers', 'Onboarding', 'OpsDashboard', 'LabSetups'],
		}),
		rejectHandover: builder.mutation<any, { id: string; reason: string }>({
			query: ({ id, reason }) => ({
				url: `/ops/handovers/${id}/reject`,
				method: 'POST',
				data: { reason },
			}),
			invalidatesTags: ['Handover', 'Handovers', 'OpsDashboard'],
		}),

		// ── Onboarding ──
		listOnboarding: builder.query<any, { status?: string } | void>({
			query: (args) => ({
				url: '/ops/onboarding',
				params: (args as any)?.status ? { status: (args as any).status } : {},
			}),
			providesTags: ['Onboarding'],
		}),
		getReadiness: builder.query<any, string>({
			query: (schoolId) => ({ url: `/ops/schools/${schoolId}/readiness` }),
			providesTags: ['Readiness'],
		}),
		goLive: builder.mutation<any, string>({
			query: (schoolId) => ({
				url: `/ops/schools/${schoolId}/go-live`,
				method: 'POST',
			}),
			invalidatesTags: ['Readiness', 'Onboarding', 'OpsDashboard'],
		}),
		holdSchool: builder.mutation<any, { schoolId: string; reason: string }>({
			query: ({ schoolId, reason }) => ({
				url: `/ops/schools/${schoolId}/hold`,
				method: 'POST',
				data: { reason },
			}),
			invalidatesTags: ['Readiness', 'Onboarding', 'OpsDashboard'],
		}),
		resumeSchool: builder.mutation<any, string>({
			query: (schoolId) => ({
				url: `/ops/schools/${schoolId}/resume`,
				method: 'POST',
			}),
			invalidatesTags: ['Readiness', 'Onboarding', 'OpsDashboard'],
		}),
		getTrainerPool: builder.query<any, string>({
			query: (schoolId) => ({ url: `/ops/schools/${schoolId}/trainer-pool` }),
			providesTags: ['TrainerPool'],
		}),
		assignTrainer: builder.mutation<any, { schoolId: string; trainerId: string }>({
			query: ({ schoolId, trainerId }) => ({
				url: `/ops/schools/${schoolId}/assign-trainer`,
				method: 'POST',
				data: { trainerId },
			}),
			invalidatesTags: ['TrainerPool', 'Readiness', 'Onboarding', 'OpsDashboard'],
		}),

		// ── Lab setup ──
		listLabSetups: builder.query<any, { status?: string } | void>({
			query: (args) => ({
				url: '/ops/lab-setups',
				params: (args as any)?.status ? { status: (args as any).status } : {},
			}),
			providesTags: ['LabSetups'],
		}),
		getLabSetup: builder.query<any, string>({
			query: (schoolId) => ({ url: `/ops/lab-setups/${schoolId}` }),
			providesTags: ['LabSetup'],
		}),
		updateLabSetup: builder.mutation<any, { schoolId: string; data: any }>({
			query: ({ schoolId, data }) => ({
				url: `/ops/lab-setups/${schoolId}`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: ['LabSetup', 'LabSetups'],
		}),
		submitLabSetup: builder.mutation<any, string>({
			query: (schoolId) => ({
				url: `/ops/lab-setups/${schoolId}/submit`,
				method: 'POST',
			}),
			invalidatesTags: ['LabSetup', 'LabSetups', 'Readiness', 'OpsDashboard'],
		}),

		// ── Inventory ──
		getInventory: builder.query<any, string>({
			query: (schoolId) => ({ url: `/ops/schools/${schoolId}/inventory` }),
			providesTags: ['Inventory'],
		}),
		updateKitReceipts: builder.mutation<
			any,
			{ schoolId: string; lines: { kitType: string; receivedQty: number }[] }
		>({
			query: ({ schoolId, lines }) => ({
				url: `/ops/schools/${schoolId}/kit-receipts`,
				method: 'PATCH',
				data: { lines },
			}),
			invalidatesTags: ['Inventory', 'Readiness', 'Onboarding'],
		}),
		createLabAsset: builder.mutation<any, { schoolId: string; data: any }>({
			query: ({ schoolId, data }) => ({
				url: `/ops/schools/${schoolId}/lab-assets`,
				method: 'POST',
				data,
			}),
			invalidatesTags: ['Inventory'],
		}),
		updateLabAsset: builder.mutation<any, { id: string; data: any }>({
			query: ({ id, data }) => ({
				url: `/ops/lab-assets/${id}`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: ['Inventory'],
		}),
		deleteLabAsset: builder.mutation<any, string>({
			query: (id) => ({ url: `/ops/lab-assets/${id}`, method: 'DELETE' }),
			invalidatesTags: ['Inventory'],
		}),
		createMaterialRequest: builder.mutation<any, any>({
			query: (data) => ({ url: '/ops/material-requests', method: 'POST', data }),
			invalidatesTags: ['Inventory', 'MaterialRequests', 'OpsDashboard'],
		}),
		updateMaterialRequestStatus: builder.mutation<
			any,
			{ id: string; status: string; reason?: string }
		>({
			query: ({ id, ...data }) => ({
				url: `/ops/material-requests/${id}/status`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: ['Inventory', 'MaterialRequests', 'OpsDashboard'],
		}),

		// ── Tickets ──
		listTickets: builder.query<
			any,
			{ status?: string; category?: string; schoolId?: string }
		>({
			query: (params) => ({ url: '/ops/tickets', params }),
			providesTags: ['Tickets'],
		}),
		getTicket: builder.query<any, string>({
			query: (id) => ({ url: `/ops/tickets/${id}` }),
			providesTags: ['Ticket'],
		}),
		updateTicketStatus: builder.mutation<
			any,
			{ id: string; status: string; note?: string; resolutionNotes?: string }
		>({
			query: ({ id, ...data }) => ({
				url: `/ops/tickets/${id}/status`,
				method: 'PATCH',
				data,
			}),
			invalidatesTags: ['Ticket', 'Tickets', 'MyTickets', 'OpsDashboard'],
		}),
		addTicketNote: builder.mutation<any, { id: string; note: string }>({
			query: ({ id, note }) => ({
				url: `/ops/tickets/${id}/notes`,
				method: 'POST',
				data: { note },
			}),
			invalidatesTags: ['Ticket'],
		}),
		// Raiser side — any role
		createTicket: builder.mutation<any, any>({
			query: (data) => ({ url: '/ops/tickets', method: 'POST', data }),
			invalidatesTags: ['MyTickets', 'Tickets', 'OpsDashboard'],
		}),
		listMyTickets: builder.query<any, void>({
			query: () => ({ url: '/ops/tickets/mine' }),
			providesTags: ['MyTickets'],
		}),
		listRaiserSchools: builder.query<any, void>({
			query: () => ({ url: '/ops/tickets/schools' }),
		}),
		confirmCloseTicket: builder.mutation<any, string>({
			query: (id) => ({ url: `/ops/tickets/${id}/confirm-close`, method: 'POST' }),
			invalidatesTags: ['Ticket', 'MyTickets', 'Tickets'],
		}),

		// ── School admin: own lab approval ──
		getOwnLabSetup: builder.query<any, void>({
			query: () => ({ url: '/school-admin/lab-setup' }),
			providesTags: ['OwnLabSetup'],
		}),
		approveOwnLab: builder.mutation<any, void>({
			query: () => ({ url: '/school-admin/lab-setup/approve', method: 'POST' }),
			invalidatesTags: ['OwnLabSetup'],
		}),
		reworkOwnLab: builder.mutation<any, string>({
			query: (reason) => ({
				url: '/school-admin/lab-setup/rework',
				method: 'POST',
				data: { reason },
			}),
			invalidatesTags: ['OwnLabSetup'],
		}),
	}),
});

export const {
	useGetOpsDashboardQuery,
	useListHandoversQuery,
	useGetHandoverQuery,
	useUpdateHandoverMutation,
	useAcceptHandoverMutation,
	useRejectHandoverMutation,
	useListOnboardingQuery,
	useGetReadinessQuery,
	useGoLiveMutation,
	useHoldSchoolMutation,
	useResumeSchoolMutation,
	useGetTrainerPoolQuery,
	useAssignTrainerMutation,
	useListLabSetupsQuery,
	useGetLabSetupQuery,
	useUpdateLabSetupMutation,
	useSubmitLabSetupMutation,
	useGetInventoryQuery,
	useUpdateKitReceiptsMutation,
	useCreateLabAssetMutation,
	useUpdateLabAssetMutation,
	useDeleteLabAssetMutation,
	useCreateMaterialRequestMutation,
	useUpdateMaterialRequestStatusMutation,
	useListTicketsQuery,
	useGetTicketQuery,
	useUpdateTicketStatusMutation,
	useAddTicketNoteMutation,
	useCreateTicketMutation,
	useListMyTicketsQuery,
	useListRaiserSchoolsQuery,
	useConfirmCloseTicketMutation,
	useGetOwnLabSetupQuery,
	useApproveOwnLabMutation,
	useReworkOwnLabMutation,
} = opsApi;
